"use client";
import { useCallback, useEffect, useState } from "react";
import type { EventBinding } from "@/lib/api/events";
import {
  bidBindingKey,
  persistBidOperation,
  removeBidOperation,
  restoreBidOperations,
  type BidOperation,
} from "@/lib/commands/bid-operation-store";

export function useBidRecovery(binding: EventBinding) {
  const [records, setRecords] = useState<BidOperation[]>([]);
  const [ready, setReady] = useState(false);
  const [available, setAvailable] = useState(false);
  const read = useCallback(
    () => restoreBidOperations(window.localStorage, binding),
    [binding],
  );
  const sync = useCallback(() => {
    try {
      setRecords(read());
      setAvailable(true);
    } catch {
      setAvailable(false);
    } finally {
      setReady(true);
    }
  }, [read]);
  useEffect(() => {
    const timer = setTimeout(sync, 0);
    const changed = (event: StorageEvent) => {
      if (!event.key || event.key.startsWith(bidBindingKey(binding))) sync();
    };
    window.addEventListener("storage", changed);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("storage", changed);
    };
  }, [binding, sync]);
  function save(record: BidOperation) {
    try {
      persistBidOperation(window.localStorage, binding, record);
      sync();
      return true;
    } catch {
      setAvailable(false);
      return false;
    }
  }
  function remove(key: string) {
    try {
      removeBidOperation(window.localStorage, binding, key);
      sync();
      return true;
    } catch {
      setAvailable(false);
      return false;
    }
  }
  return { records, ready, available, read, sync, save, remove };
}
